import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileText, RefreshCw, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, ukDate, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PageHeader, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const FILTERS = [["", "All"], ["needs_review", "Needs review"], ["processing,uploaded", "Processing"], ["confirmed", "Confirmed"], ["failed", "Failed"]];

export default function Invoices() {
  const { restaurantId, can } = useAuth();
  const isOwner = can("review_invoices");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);

  const load = () => api.get("/invoices", { params: { status: status || undefined, restaurant_id: restaurantId || undefined, q: q || undefined } })
    .then((r) => setItems(r.data)).catch((e) => { if (items === null) toast.error(errorMessage(e)); });

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (items?.some((i) => ["processing", "uploaded"].includes(i.status)) || status.includes("processing")) load();
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, restaurantId, q, items?.length]);

  const retry = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    try { await api.post(`/invoices/${id}/retry`); toast.success("Reprocessing started"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const remove = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this invoice and its extracted data?")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle="Every scanned or uploaded invoice and where it is in the workflow."
        actions={<Button asChild className="bg-emerald-600 hover:bg-emerald-700 h-11"><Link to="/scan" data-testid="invoices-scan-button">Scan / upload</Link></Button>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="invoice-filters">
          {FILTERS.map(([val, label]) => (
            <button key={val} onClick={() => setParams(val ? { status: val } : {})} data-testid={`filter-${val || "all"}`}
              className={`px-3 py-1.5 rounded-full text-sm border whitespace-nowrap transition-colors ${status === val ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{label}</button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier or invoice #" className="pl-9" data-testid="invoice-search-input" />
        </div>
      </div>

      {items === null ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <EmptyState icon={FileText} title="No invoices here" hint="Scan a paper invoice or upload a PDF to get started." testId="invoices-empty"
          action={<Button asChild variant="outline"><Link to="/scan">Go to Scan Invoice</Link></Button>} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden fade-up">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Supplier</th><th className="px-4 py-3 hidden md:table-cell">Invoice #</th><th className="px-4 py-3 hidden sm:table-cell">Date</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((inv) => (
                <tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} className="hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/invoices/${inv.id}`} className="font-medium text-slate-900 hover:text-emerald-700" data-testid={`invoice-link-${inv.id}`}>{inv.supplier_name || inv.original_filename || "Untitled"}</Link>
                    <p className="text-xs text-slate-500 md:hidden font-mono">{inv.invoice_number ? `#${inv.invoice_number}` : ""}</p>
                    {inv.split_info && <p className="text-xs text-violet-700" data-testid={`split-badge-${inv.id}`}>Invoice {inv.split_info.index} of {inv.split_info.total} in {inv.split_info.filename}</p>}
                    {inv.auto_confirmed && inv.status === "confirmed" && <p className="text-xs text-emerald-700" data-testid={`auto-badge-${inv.id}`}>Auto-confirmed</p>}
                    {inv.duplicate_of?.length > 0 && <p className="text-xs text-amber-700">Possible duplicate</p>}
                    {inv.status === "failed" && <p className="text-xs text-rose-700 max-w-xs">{inv.error_message}</p>}
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-700 hidden md:table-cell">{inv.invoice_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{ukDate(inv.invoice_date)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-800">{gbp(inv.total, inv.currency)}</td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isOwner && inv.status === "failed" && <button onClick={(e) => retry(e, inv.id)} className="p-2 text-slate-500 hover:text-emerald-700" title="Retry" data-testid={`retry-${inv.id}`}><RefreshCw className="h-4 w-4" /></button>}
                    {isOwner && <button onClick={(e) => remove(e, inv.id)} className="p-2 text-slate-400 hover:text-rose-600" title="Delete" data-testid={`delete-${inv.id}`}><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
